import React, { useMemo, useState, useCallback, useEffect, useContext, useRef } from "react";
import { ReactFlowProvider, Node, Edge } from "reactflow";
import "reactflow/dist/style.css";
import mapboxgl from 'mapbox-gl';
import along from '@turf/along';
import length from '@turf/length';
import bearing from '@turf/bearing';
import { lineString, point } from '@turf/helpers';
import {
  Box,
  Chip,
  Typography,
  Popover,
  Stack,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import { Undo, Delete } from "@mui/icons-material";

import { OrgSettingsContext } from "../../../contexts/org-settings-context";
import { CustomNode } from "./custom-node";

import { makeProjection, ringPixelsToLonLat, ensureClosedLonLatRing } from "../../../lib/geo/projection";
import type { XY } from "../../../lib/geo/types";
import type { IArea } from "../../../interfaces/common/location";

import { usePartitions } from "../../../hooks/partitions";
import { useGeo } from "../../../hooks/usegeo";
import LebanonMapBaseMapbox, { MapboxHandle } from "./lebanon-map-base-mapbox";
import LebanonMapBaseStatic from "./lebanon-map-base-static";
import PartitionsLayer from "./partition-layer";
import RFOverlay from "./react-flow-overlay";
import DrawCaptureLayer from "./draw-capture-layer";
import { Polygon } from "geojson";

export enum LocationType {
  WAREHOUSE = "warehouse",
  HOME = "home",
  SHOP = "shop",
  GENERIC = "generic"
}

export interface MapLocationItem {
  id: string | number;
  label: string;
  area: IArea | undefined;
  width?: number;
  height?: number;
  type: LocationType;
  logoUrl?: string | null;
}

export type LinkStatus = "completed" | "in_progress" | "pending";

export interface LocationsLink {
  locationA: string | number;
  locationB: string | number;
  label?: React.ReactNode;
  id?: number;
  type?: "default" | "straight" | "smoothstep",
  directed?: boolean;
  color?: string;
  status?: LinkStatus;
}

import { MapboxStyle } from "./lebanon-map-base-mapbox";
export type { MapboxStyle };

interface LebanonMapFlowProps {
  locations: MapLocationItem[];
  showPartitions: boolean;
  links?: LocationsLink[];
  width?: number;
  height?: number;
  scale?: number;  // D3 scale (still used for D3 projection fallback)
  zoom?: number;   // Mapbox zoom level (7.5 for normal, 8.0 for zoomed in)
  showDistricts?: boolean;
  branchId?: string | number; // optional: for filtering/saving partitions by branch
  onNodeClick?: (id: string, e: React.MouseEvent) => void;
  onLinkUpdate?: (link: LocationsLink, newLabel: string) => Promise<void>;
  onLinkDelete?: (link: LocationsLink) => Promise<void>;
  addLinkMode?: boolean;
  onToggleAddLink?: () => void;
  loading?: boolean;
  useStaticMap?: boolean;  // Use static SVG map instead of Mapbox GL
  useNativeRoutes?: boolean; // Use native Mapbox rendering instead of ReactFlow
  interactive?: boolean;  // Enable zoom/pan controls on Mapbox map
  mapStyle?: MapboxStyle;  // Mapbox map style
  showBorder?: boolean;  // Show country border (default true)
}

const nodeTypes = { "custom-node": CustomNode };

function LebanonMapFlowInner({
  locations,
  links,
  width = 400,
  height = 400,
  scale = 7000,
  zoom = 7.5,
  showDistricts = true,
  branchId,
  onNodeClick,
  onLinkUpdate,
  onLinkDelete,
  addLinkMode,
  onToggleAddLink,
  showPartitions,
  loading = false,
  useStaticMap = false,
  useNativeRoutes = false,
  interactive = false,
  mapStyle = 'streets-v12',
  showBorder = true
}: LebanonMapFlowProps) {
  const { color } = useContext(OrgSettingsContext);
  const mapboxRef = React.useRef<MapboxHandle>(null);
  const animationRef = useRef<number | null>(null);
  const logoMarkersRef = useRef<mapboxgl.Marker[]>([]);

  // Fetch map borders/districts first (so other layers wait)
  const { borderFC, adm2FC, loading: mapLoading } = useGeo({ 
    iso: "LBN", 
    withADM2: showDistricts, 
    withBorder: true 
  });

  // --- Delete mode + popover for partitions ---
  const [deleteMode, setDeleteMode] = useState(false);
  const [deletePopover, setDeletePopover] = useState<{
    open: boolean;
    pos: { top: number; left: number } | null;
    partitionId: string | null;
  }>({ open: false, pos: null, partitionId: null });

  const openDeleteFor = (evt: React.MouseEvent, id: string) => {
    if (!deleteMode) return;
    evt.stopPropagation();
    setDeletePopover({
      open: true,
      pos: { top: evt.clientY, left: evt.clientX },
      partitionId: id,
    });
  };

  const closeDeletePopover = () =>
    setDeletePopover({ open: false, pos: null, partitionId: null });

  // Keep D3 projection for compatibility with other components
  const project = useMemo(
    () => makeProjection(width, height, scale, [35.9, 33.9]),
    [width, height, scale]
  );

  // Track when map is ready - no delays, pure callback
  const [mapReady, setMapReady] = useState(false);
  const [mapReadyTrigger, setMapReadyTrigger] = useState(0);
  
  // Reset mapReady when mapStyle changes (map will be recreated)
  useEffect(() => {
    setMapReady(false);
  }, [mapStyle]);
  
  const handleMapReady = useCallback(() => {
    setMapReady(true);
    setMapReadyTrigger(prev => prev + 1);
  }, []);

  // Native Mapbox route rendering
  useEffect(() => {
    console.log('Native routes effect triggered:', { useNativeRoutes, mapReady, useStaticMap, locationsCount: locations.length, linksCount: links?.length });
    
    if (!useNativeRoutes || !mapReady || useStaticMap) {
      return;
    }
    
    const map = mapboxRef.current?.getMap();
    if (!map) {
      console.log('Map instance not available');
      return;
    }

    // SVG icon definitions (modern, realistic design)
    const svgIcons: Record<string, string> = {
      'icon-warehouse': `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
        <defs>
          <linearGradient id="wg" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style="stop-color:#1976d2"/>
            <stop offset="100%" style="stop-color:#0d47a1"/>
          </linearGradient>
          <filter id="ws" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.3"/>
          </filter>
        </defs>
        <circle cx="24" cy="24" r="22" fill="url(#wg)" filter="url(#ws)"/>
        <circle cx="24" cy="24" r="20" fill="none" stroke="#fff" stroke-width="2" opacity="0.3"/>
        <rect x="10" y="18" width="28" height="16" rx="1" fill="#fff"/>
        <path d="M10 18l14-8 14 8" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        <rect x="14" y="22" width="6" height="8" rx="0.5" fill="#1976d2" opacity="0.7"/>
        <rect x="28" y="22" width="6" height="8" rx="0.5" fill="#1976d2" opacity="0.7"/>
        <rect x="21" y="26" width="6" height="8" rx="0.5" fill="#0d47a1"/>
      </svg>`,
      'icon-store': `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
        <defs>
          <linearGradient id="sg" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style="stop-color:#9c27b0"/>
            <stop offset="100%" style="stop-color:#6a1b9a"/>
          </linearGradient>
          <filter id="ss" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.3"/>
          </filter>
        </defs>
        <circle cx="24" cy="24" r="22" fill="url(#sg)" filter="url(#ss)"/>
        <circle cx="24" cy="24" r="20" fill="none" stroke="#fff" stroke-width="2" opacity="0.3"/>
        <rect x="10" y="18" width="28" height="16" rx="1" fill="#fff"/>
        <path d="M8 14h32l-3 6H11l-3-6z" fill="#fff"/>
        <ellipse cx="12" cy="17" rx="3" ry="2.5" fill="#e1bee7"/>
        <ellipse cx="20" cy="17" rx="3" ry="2.5" fill="#ce93d8"/>
        <ellipse cx="28" cy="17" rx="3" ry="2.5" fill="#e1bee7"/>
        <ellipse cx="36" cy="17" rx="3" ry="2.5" fill="#ce93d8"/>
        <rect x="20" y="24" width="8" height="10" rx="0.5" fill="#6a1b9a"/>
        <circle cx="26" cy="29" r="1" fill="#ffd54f"/>
      </svg>`,
      'icon-person': `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
        <defs>
          <linearGradient id="pg" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style="stop-color:#03a9f4"/>
            <stop offset="100%" style="stop-color:#0277bd"/>
          </linearGradient>
          <filter id="ps" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.3"/>
          </filter>
        </defs>
        <circle cx="24" cy="24" r="22" fill="url(#pg)" filter="url(#ps)"/>
        <circle cx="24" cy="24" r="20" fill="none" stroke="#fff" stroke-width="2" opacity="0.3"/>
        <circle cx="24" cy="16" r="6" fill="#fff"/>
        <path d="M13 38c0-6.1 4.9-11 11-11s11 4.9 11 11" fill="#fff"/>
        <circle cx="24" cy="16" r="4" fill="#b3e5fc"/>
      </svg>`,
      'icon-motorcycle': `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
        <defs>
          <linearGradient id="mg" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style="stop-color:#ff9800"/>
            <stop offset="100%" style="stop-color:#e65100"/>
          </linearGradient>
          <filter id="ms" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.3"/>
          </filter>
        </defs>
        <circle cx="24" cy="24" r="22" fill="url(#mg)" filter="url(#ms)"/>
        <circle cx="24" cy="24" r="20" fill="none" stroke="#fff" stroke-width="2" opacity="0.3"/>
        <rect x="8" y="18" width="22" height="14" rx="2" fill="#fff"/>
        <path d="M30 18h6l4 8v6h-10v-14z" fill="#e3f2fd"/>
        <rect x="32" y="20" width="6" height="6" rx="1" fill="#90caf9" stroke="#64b5f6" stroke-width="0.5"/>
        <circle cx="14" cy="32" r="4" fill="#424242"/>
        <circle cx="14" cy="32" r="2" fill="#757575"/>
        <circle cx="36" cy="32" r="4" fill="#424242"/>
        <circle cx="36" cy="32" r="2" fill="#757575"/>
        <rect x="10" y="22" width="8" height="6" rx="1" fill="#ffcc80"/>
      </svg>`
    };

    // Load SVG icons into Mapbox
    const loadSvgIcons = async () => {
      for (const [name, svg] of Object.entries(svgIcons)) {
        if (map.hasImage(name)) continue;
        
        await new Promise<void>((resolve) => {
          const img = new Image(48, 48);
          img.onload = () => {
            if (!map.hasImage(name)) {
              map.addImage(name, img, { sdf: false, pixelRatio: 2 });
            }
            resolve();
          };
          img.onerror = () => {
            console.error(`Failed to load icon: ${name}`);
            resolve();
          };
          img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
        });
      }
      console.log('All SVG icons loaded');
    };

    // We'll use HTML markers for logos instead of trying to load them as Mapbox images
    // This avoids all CORS issues
    const loadMerchantLogos = async () => {
      // No-op - we'll use HTML markers instead
      console.log('Merchant logos will be rendered as HTML markers');
    };

    // Function to add route layers
    const addRouteLayers = async () => {
      // Load SVG icons and merchant logos first
      await loadSvgIcons();
      await loadMerchantLogos();
      console.log('addRouteLayers called, isStyleLoaded:', map.isStyleLoaded());

      // Remove existing route layers if they exist
      const layersToRemove = [
        'route-lines-outline', 'route-lines', 'route-lines-dashed', 'route-lines-pending',
        'route-arrows', 'route-status-badge', 'route-status-icon', 'route-labels',
        'location-markers', 'location-labels', 'merchant-logo-markers',
        'motorcycle-icon'
      ];
      layersToRemove.forEach(layerId => {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
      });
      const sourcesToRemove = ['routes', 'route-midpoints', 'locations', 'motorcycle', 'merchant-logos'];
      sourcesToRemove.forEach(sourceId => {
        if (map.getSource(sourceId)) map.removeSource(sourceId);
      });
      

      // Build GeoJSON for route lines
      const routeFeatures: any[] = [];
      if (links && links.length > 0) {
        links.forEach((link, idx) => {
          const locA = locations.find(l => String(l.id) === String(link.locationA));
          const locB = locations.find(l => String(l.id) === String(link.locationB));
          
          if (locA?.area?.long && locA?.area?.lat && locB?.area?.long && locB?.area?.lat) {
            // Determine color based on status
            let edgeColor = link.color || color;
            let dashArray: number[] | null = null;
            let opacity = 0.9;
            
            if (link.status === 'completed') {
              edgeColor = '#2e7d32'; // Green
            } else if (link.status === 'in_progress') {
              edgeColor = '#ed6c02'; // Orange
              dashArray = [10, 5]; // Dashed line for in-progress
            } else if (link.status === 'pending') {
              edgeColor = '#757575'; // Grey
              dashArray = [5, 5]; // Dotted line for pending
              opacity = 0.6;
            }
            
            routeFeatures.push({
              type: 'Feature',
              geometry: {
                type: 'LineString',
                coordinates: [
                  [locA.area.long, locA.area.lat],
                  [locB.area.long, locB.area.lat]
                ]
              },
              properties: {
                color: edgeColor,
                label: typeof link.label === 'string' ? link.label : '',
                id: idx,
                status: link.status || 'pending',
                dashArray: dashArray ? dashArray.join(',') : '',
                opacity: opacity
              }
            });
          }
        });
      }
      console.log('Route features:', routeFeatures);

      // Build GeoJSON for location markers
      const locationFeatures: any[] = [];
      locations.forEach(loc => {
        if (loc.area?.long && loc.area?.lat) {
          locationFeatures.push({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [loc.area.long, loc.area.lat]
            },
            properties: {
              label: loc.label,
              type: loc.type,
              id: String(loc.id),
              logoUrl: loc.logoUrl
            }
          });
        }
      });
      console.log('Location features:', locationFeatures);

      // Add route lines source and layers
      if (routeFeatures.length > 0) {
        console.log('Adding route source and layers...');
        map.addSource('routes', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: routeFeatures
          }
        });

        // Outline/shadow for better visibility
        map.addLayer({
          id: 'route-lines-outline',
          type: 'line',
          source: 'routes',
          paint: {
            'line-color': '#ffffff',
            'line-width': 8,
            'line-opacity': 0.9
          }
        });

        // Main route line - solid for completed, dashed for others
        map.addLayer({
          id: 'route-lines',
          type: 'line',
          source: 'routes',
          paint: {
            'line-color': ['get', 'color'],
            'line-width': 5,
            'line-opacity': 1
          }
        });

        // Dashed pattern overlay for in-progress
        map.addLayer({
          id: 'route-lines-dashed',
          type: 'line',
          source: 'routes',
          filter: ['==', ['get', 'status'], 'in_progress'],
          paint: {
            'line-color': '#fff',
            'line-width': 5,
            'line-dasharray': [2, 3],
            'line-opacity': 0.5
          }
        });

        // Dotted pattern overlay for pending
        map.addLayer({
          id: 'route-lines-pending',
          type: 'line',
          source: 'routes',
          filter: ['==', ['get', 'status'], 'pending'],
          paint: {
            'line-color': '#fff',
            'line-width': 5,
            'line-dasharray': [1, 2],
            'line-opacity': 0.6
          }
        });

        // Direction arrows along the line - enhanced for better visibility
        map.addLayer({
          id: 'route-arrows',
          type: 'symbol',
          source: 'routes',
          layout: {
            'symbol-placement': 'line',
            'symbol-spacing': 120,  // More spacing for larger arrows
            'text-field': '▶',  // Using solid arrow for better visibility
            'text-size': 40,  // Extra large size for maximum visibility
            'text-keep-upright': false,
            'text-allow-overlap': true,
            'text-ignore-placement': true
          },
          paint: {
            'text-color': ['get', 'color'],
            'text-halo-color': '#ffffff',  // White halo for contrast
            'text-halo-width': 4,  // Extra thick halo for better visibility
            'text-halo-blur': 1  // Slight blur for smooth edges
          }
        });

        // Calculate midpoints for status badges
        const midpointFeatures: any[] = routeFeatures.map((f: any) => {
          const coords = f.geometry.coordinates;
          const midLng = (coords[0][0] + coords[1][0]) / 2;
          const midLat = (coords[0][1] + coords[1][1]) / 2;
          return {
            type: 'Feature' as const,
            geometry: { type: 'Point' as const, coordinates: [midLng, midLat] },
            properties: { ...f.properties }
          };
        });

        map.addSource('route-midpoints', {
          type: 'geojson',
          data: { type: 'FeatureCollection' as const, features: midpointFeatures }
        });

        // Status indicator badge background
        map.addLayer({
          id: 'route-status-badge',
          type: 'circle',
          source: 'route-midpoints',
          paint: {
            'circle-radius': 14,
            'circle-color': ['get', 'color'],
            'circle-stroke-color': '#fff',
            'circle-stroke-width': 3
          }
        });

        // Status icon - clear icons for each status
        map.addLayer({
          id: 'route-status-icon',
          type: 'symbol',
          source: 'route-midpoints',
          layout: {
            'text-field': [
              'case',
              ['==', ['get', 'status'], 'completed'], '✓',
              ['==', ['get', 'status'], 'in_progress'], '◐',
              '◷'
            ],
            'text-size': 14,
            'text-allow-overlap': true,
            'text-ignore-placement': true
          },
          paint: {
            'text-color': '#fff'
          }
        });

        // Route segment labels
        map.addLayer({
          id: 'route-labels',
          type: 'symbol',
          source: 'route-midpoints',
          layout: {
            'text-field': ['get', 'label'],
            'text-size': 11,
            'text-offset': [0, 2.5],
            'text-anchor': 'top'
          },
          paint: {
            'text-color': '#333',
            'text-halo-color': '#fff',
            'text-halo-width': 2
          }
        });
        console.log('Route layers added');
      }

      // Add location markers using custom SVG icons
      if (locationFeatures.length > 0) {
        console.log('Adding location markers with SVG icons...');
        
        map.addSource('locations', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: locationFeatures
          }
        });

        // Location markers using SVG icons (logos will be added as a separate layer)
        map.addLayer({
          id: 'location-markers',
          type: 'symbol',
          source: 'locations',
          layout: {
            'icon-image': [
              'match',
              ['get', 'type'],
              'warehouse', 'icon-warehouse',
              'home', 'icon-person',
              'shop', 'icon-store',
              'icon-person' // default
            ],
            'icon-size': 1.3,
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'icon-anchor': 'center'
          }
        });

        // Add merchant logos as a separate layer on top
        console.log('Checking for logos to display...');
        console.log('All location features:', locationFeatures.map(f => ({
          id: f.properties.id,
          label: f.properties.label,
          type: f.properties.type,
          logoUrl: f.properties.logoUrl,
          hasImage: map.hasImage(`logo-${f.properties.id}`)
        })));
        
        const locationsWithLogos = locationFeatures.filter(f => 
          f.properties.type === 'shop' && 
          f.properties.logoUrl && 
          f.properties.logoUrl !== '' &&
          map.hasImage(`logo-${f.properties.id}`)
        );
        
        console.log('Filtered locations with logos:', locationsWithLogos.length);
        
        if (locationsWithLogos.length > 0) {
          console.log('Adding logo layer for:', locationsWithLogos.map(f => ({
            id: f.properties.id,
            label: f.properties.label,
            imageId: `logo-${f.properties.id}`
          })));
          
          map.addSource('merchant-logos', {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: locationsWithLogos
            }
          });

          map.addLayer({
            id: 'merchant-logo-markers',
            type: 'symbol',
            source: 'merchant-logos',
            layout: {
              'icon-image': ['concat', 'logo-', ['get', 'id']],
              'icon-size': 0.8,
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
              'icon-anchor': 'center'
            }
          });
          console.log('Logo layer added successfully');
        } else {
          console.log('No logos to display - reasons:');
          locationFeatures.forEach(f => {
            if (f.properties.type === 'shop') {
              console.log(`  ${f.properties.label}:`, {
                hasLogoUrl: !!f.properties.logoUrl,
                logoUrl: f.properties.logoUrl,
                imageLoaded: map.hasImage(`logo-${f.properties.id}`),
                imageId: `logo-${f.properties.id}`
              });
            }
          });
        }

        // Location labels - natural placement with better visibility
        map.addLayer({
          id: 'location-labels',
          type: 'symbol',
          source: 'locations',
          layout: {
            'text-field': ['get', 'label'],
            'text-size': 14,
            'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
            'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
            'text-radial-offset': 1.8,
            'text-justify': 'auto',
            'text-max-width': 10,
            'text-allow-overlap': false,
            'text-ignore-placement': false,
            'text-letter-spacing': 0.02
          },
          paint: {
            'text-color': '#000000',
            'text-halo-color': '#ffffff',
            'text-halo-width': 3,
            'text-halo-blur': 0
          }
        });
        
        console.log('Location markers added');
      }

      // Clean up existing logo markers
      logoMarkersRef.current.forEach(marker => marker.remove());
      logoMarkersRef.current = [];

      // Add HTML markers for logos (merchant and warehouse/organization logos)
      const locationsWithLogos = locations.filter(loc => loc.logoUrl && loc.area?.long && loc.area?.lat);
      locationsWithLogos.forEach(loc => {
        // Create HTML element for the logo
        const el = document.createElement('div');
        el.style.width = '40px';
        el.style.height = '40px';
        el.style.borderRadius = '50%';
        el.style.overflow = 'hidden';
        el.style.backgroundColor = '#ffffff';
        el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
        el.style.cursor = 'pointer';
        el.style.backgroundImage = `url(${loc.logoUrl})`;
        el.style.backgroundSize = 'cover';
        el.style.backgroundPosition = 'center';
        
        // Different border colors for different location types
        if (loc.type === 'shop') {
          el.style.border = '3px solid #f44336'; // Red for shops/merchants
        } else if (loc.type === 'warehouse') {
          el.style.border = '3px solid #1976d2'; // Blue for warehouses
        } else {
          el.style.border = '3px solid #757575'; // Grey for others
        }
        
        // Add marker to map
        const marker = new mapboxgl.Marker(el)
          .setLngLat([loc.area!.long!, loc.area!.lat!])
          .addTo(map);
        
        logoMarkersRef.current.push(marker);
        console.log('Added HTML marker for:', loc.label, 'type:', loc.type);
      });

      // Add animated motorcycle for in-progress routes
      console.log('Looking for in-progress route. All route statuses:', routeFeatures.map(f => f.properties.status));
      const inProgressRoute = routeFeatures.find(f => f.properties.status === 'in_progress');
      console.log('In-progress route found:', inProgressRoute);
      
      if (inProgressRoute && inProgressRoute.geometry.coordinates.length >= 2) {
        console.log('Adding motorcycle animation for in-progress route:', inProgressRoute.geometry.coordinates);
        
        const coords = inProgressRoute.geometry.coordinates as [number, number][];
        const routeLine = lineString(coords);
        const routeLength = length(routeLine, { units: 'kilometers' });
        console.log('Route length:', routeLength, 'km');
        
        // Initial position at start of route
        const startPoint = point(coords[0]);
        const endPoint = point(coords[1]);
        const initialBearing = bearing(startPoint, endPoint);
        
        // Add motorcycle source
        map.addSource('motorcycle', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: coords[0]
            },
            properties: {
              bearing: initialBearing
            }
          }
        });

        // Motorcycle marker using SVG icon
        map.addLayer({
          id: 'motorcycle-icon',
          type: 'symbol',
          source: 'motorcycle',
          layout: {
            'icon-image': 'icon-motorcycle',
            'icon-size': 1.4,
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'icon-anchor': 'center'
          }
        });

        // Animation variables
        let progress = 0;
        const animationSpeed = 0.0015; // Slower, more realistic speed

        const animateMotorcycle = () => {
          // Safety check: stop animation if map is no longer valid
          try {
            if (!map || !map.getSource) {
              animationRef.current = null;
              return;
            }

            const source = map.getSource('motorcycle') as mapboxgl.GeoJSONSource;
            if (!source) {
              // Source no longer exists, stop animation
              animationRef.current = null;
              return;
            }
            
            // Skip this frame if style is not loaded, but continue animation
            if (!map.isStyleLoaded()) {
              animationRef.current = requestAnimationFrame(animateMotorcycle);
              return;
            }

            // Calculate current position along the route
            const currentDistance = progress * routeLength;
            const currentPoint = along(routeLine, currentDistance, { units: 'kilometers' });
            
            // Calculate bearing for rotation
            const nextProgress = Math.min(progress + 0.01, 1);
            const nextDistance = nextProgress * routeLength;
            const nextPoint = along(routeLine, nextDistance, { units: 'kilometers' });
            const currentBearing = bearing(
              point(currentPoint.geometry.coordinates as [number, number]),
              point(nextPoint.geometry.coordinates as [number, number])
            );

            // Update motorcycle position
            source.setData({
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: currentPoint.geometry.coordinates
              },
              properties: {
                bearing: currentBearing
              }
            });

            // Update progress (loop back to start)
            progress += animationSpeed;
            if (progress >= 1) {
              progress = 0;
            }

            // Continue animation
            animationRef.current = requestAnimationFrame(animateMotorcycle);
          } catch (e) {
            // Map was likely destroyed, stop animation
            console.log('Animation stopped due to map change');
            animationRef.current = null;
          }
        };

        // Start animation
        console.log('Starting motorcycle animation');
        animateMotorcycle();
      } else {
        console.log('No in-progress route found or route has insufficient coordinates');
      }

      // Auto-fit bounds to show all locations
      if (locationFeatures.length > 0) {
        console.log('Fitting bounds...');
        const coordinates = locationFeatures.map(f => f.geometry.coordinates);
        const bounds = coordinates.reduce((bounds, coord) => {
          return bounds.extend(coord as [number, number]);
        }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));

        map.fitBounds(bounds, {
          padding: { top: 80, bottom: 80, left: 80, right: 80 },
          maxZoom: 11,
          duration: 1000
        });
      }
    };

    // Try to add layers - use small delay to ensure map is fully ready
    const timeoutId = setTimeout(() => {
      if (map.isStyleLoaded()) {
        addRouteLayers();
      } else {
        console.log('Style not loaded, waiting for load event');
        map.once('load', addRouteLayers);
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      // Cancel motorcycle animation
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      // Cleanup on unmount
      try {
        if (map && map.isStyleLoaded()) {
          const layersToRemove = [
            'route-lines-outline', 'route-lines', 'route-lines-dashed', 'route-lines-pending',
            'route-arrows', 'route-status-badge', 'route-status-icon', 'route-labels',
            'location-markers', 'location-labels', 'merchant-logo-markers',
            'motorcycle-icon'
          ];
          layersToRemove.forEach(layerId => {
            if (map.getLayer(layerId)) map.removeLayer(layerId);
          });
          const sourcesToRemove = ['routes', 'route-midpoints', 'locations', 'motorcycle', 'merchant-logos'];
          sourcesToRemove.forEach(sourceId => {
            if (map.getSource(sourceId)) map.removeSource(sourceId);
          });
          
          // Clean up logo markers
          logoMarkersRef.current.forEach(marker => marker.remove());
          logoMarkersRef.current = [];
        }
      } catch (e) {
        console.log('Cleanup error:', e);
      }
    };
  }, [useNativeRoutes, mapReady, useStaticMap, locations, links, color, mapStyle]);

  // Nodes - use Mapbox's native lat/lng projection for dynamic map, D3 for static
  // Only create nodes when map is ready to avoid position jumps
  const rfNodes: Node[] = useMemo(() => {
    const nodes: Node[] = [];
    
    if (useStaticMap) {
      // For static map, use D3 projection and map is ready immediately
      if (!mapReady) return nodes;
      
      locations.forEach((l) => {
        const a = l.area;
        if (!a || !a.long || !a.lat) return;
        
        // Use D3 projection for static map
        const projected = project([a.long, a.lat]);
        if (!projected) return;
        const [x, y] = projected;
        
        nodes.push({
          id: String(l.id),
          type: "custom-node",
          position: { x: x - 12, y: y - 12 },
          data: { label: l.label, id: String(l.id), onClick: (evt: React.MouseEvent) => onNodeClick && onNodeClick(String(l.id), evt), type: l.type, logoUrl: l.logoUrl },
          draggable: false,
          selectable: true,
        });
      });
    } else {
      // For Mapbox, use native Mapbox projection
      const map = mapboxRef.current?.getMap();
      
      // Don't render nodes until map is ready to avoid position jumps
      if (!map || !mapReady) return nodes;
      
      locations.forEach((l) => {
        const a = l.area;
        if (!a || !a.long || !a.lat) return;
        
        // Use Mapbox's native projection
        // Mapbox uses [longitude, latitude] order
        const point = map.project([a.long, a.lat]);
        const x = point.x;
        const y = point.y;
        
        nodes.push({
          id: String(l.id),
          type: "custom-node",
          position: { x: x - 12, y: y - 12 },
          data: { label: l.label, id: String(l.id), onClick: (evt: React.MouseEvent) => onNodeClick && onNodeClick(String(l.id), evt), type: l.type, logoUrl: l.logoUrl },
          draggable: false,
          selectable: true,
        });
      });
    }
    return nodes;
  }, [locations, onNodeClick, mapboxRef, mapReady, useStaticMap, project]);

  // Edges (same logic you had)
  const [edges, setEdges] = useState<Edge[]>([]);
  useEffect(() => {
    const newEdges = links
      ? (links
          .map((link) => {
            const sourceNode = rfNodes.find((n) => n.id == link.locationA);
            const targetNode = rfNodes.find((n) => n.id == link.locationB);
            if (!sourceNode || !targetNode) return null;

            let sourceHandle = "";
            let targetHandle = "";

            // Calculate horizontal and vertical differences
            const dx = targetNode.position.x - sourceNode.position.x;
            const dy = targetNode.position.y - sourceNode.position.y;

            // Determine handles based on relative positions
            if (Math.abs(dx) > Math.abs(dy)) {
              // Horizontal difference is greater - use left/right handles
              if (dx > 0) {
                // Target is to the right of source
                sourceHandle = "source-right";
                targetHandle = "target-left";
              } else {
                // Target is to the left of source
                sourceHandle = "source-left";
                targetHandle = "target-right";
              }
            } else {
              // Vertical difference is greater - use top/bottom handles
              if (dy > 0) {
                // Target is below source
              sourceHandle = "source-bottom";
              targetHandle = "target-top";
              } else {
                // Target is above source
              sourceHandle = "source-top";
              targetHandle = "target-bottom";
              }
            }

            return {
              id: `${link.locationA}-${link.locationB}`,
              source: `${link.locationA}`,
              target: `${link.locationB}`,
              type: link.type ?? "default",
              style: { strokeWidth: 2, stroke: link.color ?? color },
              sourceHandle,
              targetHandle,
              label: link.label,
              labelStyle: {
                transform: 'translateY(-30px)',
                fill: '#333',
                fontWeight: 500,
                fontSize: 12
              },
              labelBgStyle: {
                fill: '#fff',
                fillOpacity: 0.95,
                transform: 'translateY(-30px)'
              },
              labelBgPadding: [6, 3],
              labelBgBorderRadius: 4,
              focusable: true,
              markerEnd: link.directed && {
                strokeWidth: 5
              },
              data: { link }, // Store link data for API callbacks
            } as Edge;
          })
          .filter(Boolean) as Edge[])
      : [];
    setEdges(newEdges);
  }, [links, rfNodes, color]);

  // Edge popover
  const [edgePopover, setEdgePopover] = useState<{
    open: boolean;
    pos: { top: number; left: number } | null;
    edge: Edge | null;
  }>({ open: false, pos: null, edge: null });
  const [edgeInputValue, setEdgeInputValue] = useState('');
  const [edgeLoading, setEdgeLoading] = useState(false);

  const handleEdgeClick = useCallback((e: React.MouseEvent, edge: Edge) => {
    e.stopPropagation();
    setEdgePopover({ open: true, pos: { top: e.clientY, left: e.clientX }, edge });
    setEdgeInputValue(edge.label ? String(edge.label) : '');
    setEdgeLoading(false);
  }, []);
  const closeEdgePopover = useCallback(() => {
    setEdgePopover({ open: false, pos: null, edge: null });
    setEdgeInputValue('');
    setEdgeLoading(false);
  }, []);
  const updateEdgeLabel = useCallback(
    async (newLabel: string) => {
      if (!edgePopover.edge || edgeLoading) return;
      const link = edgePopover.edge.data?.link as LocationsLink | undefined;

      // If API callback is provided, call it first
      if (onLinkUpdate && link) {
        setEdgeLoading(true);
        try {
          await onLinkUpdate(link, newLabel);
          // API call succeeded, update local state
          setEdges((prev) =>
            prev.map((ed) => (ed.id === edgePopover.edge?.id ? { ...ed, label: newLabel } : ed))
          );
          closeEdgePopover();
        } catch (error) {
          console.error('Failed to update link:', error);
          setEdgeLoading(false);
          return;
        }
      } else {
        setEdges((prev) =>
          prev.map((ed) => (ed.id === edgePopover.edge?.id ? { ...ed, label: newLabel } : ed))
        );
        closeEdgePopover();
      }
    },
    [edgePopover.edge, edgeLoading, closeEdgePopover, onLinkUpdate]
  );
  const deleteEdge = useCallback(async () => {
    if (!edgePopover.edge || edgeLoading) return;

    const link = edgePopover.edge.data?.link as LocationsLink | undefined;

    if (onLinkDelete && link) {
      setEdgeLoading(true);
      try {
        await onLinkDelete(link);
        // API call succeeded, update local state
        setEdges((prev) => prev.filter((ed) => ed.id !== edgePopover.edge?.id));
        closeEdgePopover();
      } catch (error) {
        console.error('Failed to delete link:', error);
        // Don't update local state if API call fails
        setEdgeLoading(false);
        return;
      }
    } else {
      // No API callback, just update local state
      setEdges((prev) => prev.filter((ed) => ed.id !== edgePopover.edge?.id));
      closeEdgePopover();
    }
  }, [edgePopover.edge, edgeLoading, closeEdgePopover, onLinkDelete]);

  // Partitions: server + drawing (only fetch if showPartitions is true)
  // Stable function reference to get Mapbox map
  const getMapboxMap = useCallback(() => mapboxRef.current?.getMap() || null, []);

  const {
    serverPartitions,
    setServerPartitions,
    addFromServer,
    reload: reloadPartitions,
    postPartition,
    deletePartition, // delete API
  } = usePartitions(project, color, branchId, showPartitions, getMapboxMap);

  // Reload partitions when map becomes ready (only once)
  useEffect(() => {
    if (mapReadyTrigger > 0 && showPartitions) {
      reloadPartitions();
    }
  }, [mapReadyTrigger, showPartitions, reloadPartitions]);

  const [partitionMode, setPartitionMode] = useState(false);
  const [currentStroke, setCurrentStroke] = useState<XY[] | null>(null);

  const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!partitionMode) return;
    const svg = e.currentTarget as SVGSVGElement;
    svg.setPointerCapture(e.pointerId);
    const rect = svg.getBoundingClientRect();
    setCurrentStroke([{ x: e.clientX - rect.left, y: e.clientY - rect.top }]);
  };

  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!partitionMode || !currentStroke) return;
    const svg = e.currentTarget as SVGSVGElement;
    const rect = svg.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const last = currentStroke[currentStroke.length - 1];
    if (!last || Math.hypot(x - last.x, y - last.y) > 3) {
      setCurrentStroke((prev) => (prev ? [...prev, { x, y }] : prev));
    }
  };

  const undoLastPartition = useCallback(() => {
    if (currentStroke && currentStroke.length > 0) {
      setCurrentStroke(null);
      return;
    }
    // Remove last server partition locally (does NOT delete in backend)
    setServerPartitions((prev) => (prev.length ? prev.slice(0, -1) : prev));
  }, [currentStroke, setServerPartitions]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        undoLastPartition();
      }
      if (e.key === "Escape" && currentStroke) {
        setCurrentStroke(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undoLastPartition, currentStroke]);

  const onPointerUp = async (e: React.PointerEvent<SVGSVGElement>) => {
    if (!partitionMode) return;
    const svg = e.currentTarget as SVGSVGElement;
    try {
      svg.releasePointerCapture(e.pointerId);
    } catch {}
    if (currentStroke && currentStroke.length > 2) {
      // Convert pixels to lat/lng using Mapbox projection (if available)
      const map = mapboxRef.current?.getMap();
      let ringLonLat: [number, number][];
      
      if (map && mapReady) {
        // Use Mapbox's unproject for accurate conversion
        ringLonLat = currentStroke.map(({ x, y }) => {
          const lngLat = map.unproject([x, y]);
          return [lngLat.lng, lngLat.lat] as [number, number];
        });
      } else {
        // Fallback to D3 projection
        ringLonLat = ringPixelsToLonLat(project, currentStroke);
      }
      
      ringLonLat = ensureClosedLonLatRing(ringLonLat);
      const geom: Polygon = { type: "Polygon", coordinates: [ringLonLat] };

      try {
        const resp = await postPartition({
          branch: branchId ?? (locations[0]?.id || 0),
          label: "",
          color_hex: color,
          geom,
        });
        addFromServer(resp);
      } catch (err: any) {
        const msg =
          err?.response?.data?.non_field_errors?.[0] ||
          err?.response?.data?.geom?.[0] ||
          err?.response?.data?.detail ||
          "Failed to save partition.";
        console.error("Save partition error:", err, msg);
      }
    }
    setCurrentStroke(null);
  };

  const confirmDeletePartition = async () => {
    const id = deletePopover.partitionId;
    if (!id) return;
    try {
      await deletePartition(id);
    } finally {
      closeDeletePopover();
    }
  };

  return (
    <div>
      {showDistricts && (
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 6,
            marginBottom: 2,
          }}
        >
          {Array.from(
            new Set(
              locations
                .filter((loc) => loc.area && loc.area.district !== "")
                .map((loc) => loc.area!.district)
            )
          ).map((item) => (
            <Chip key={item} label={item} />
          ))}
        </div>
      )}

      {/* Mode chips */}
      <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
        {showPartitions && <Chip
          size="small"
          color={partitionMode ? "primary" : "default"}
          label={partitionMode ? "Partition Mode (Drawing…)" : "Partition Mode"}
          onClick={() => {
            setDeleteMode(false);
            setPartitionMode((m) => !m);
          }}
        />}
        {showPartitions && <Chip
          size="small"
          icon={<Delete fontSize="small" />}
          color={deleteMode ? "error" : "default"}
          label={deleteMode ? "Delete Partitions (ON)" : "Delete Partitions"}
          onClick={() => {
            setPartitionMode(false);
            setDeleteMode((m) => !m);
          }}
          variant={deleteMode ? "filled" : "outlined"}
        />}
        {/* Add Link chip (controlled by parent via props) */}
        {showPartitions && <Chip
          size="small"
          label={onToggleAddLink ? (addLinkMode ? 'Add Link: ON' : 'Add Link') : 'Add Link'}
          color={addLinkMode ? 'primary' : 'default'}
          onClick={() => onToggleAddLink && onToggleAddLink()}
          variant={addLinkMode ? 'filled' : 'outlined'}
        />}
        {partitionMode && showPartitions && (
          <Typography variant="caption" color="text.secondary">
            Draw a region on the map to create a partition.
          </Typography>
        )}
      </Box>

      {showPartitions && <Chip
        size="small"
        icon={<Undo fontSize="small" />}
        label={currentStroke ? "Undo stroke" : "Undo region"}
        onClick={undoLastPartition}
        disabled={!currentStroke && serverPartitions.length === 0}
        variant="outlined"
      />}

      <div
        style={{
          position: "relative",
          width,
          height,
          background: `radial-gradient(${color + "30"},#fff,#fff)`,
          padding: 6,
          margin: "auto",
        }}
      >

        {/* Loading indicator for map borders */}
        {(mapLoading || loading) && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            width={width}
            height={height}
            position="absolute"
            top={0}
            left={0}
            zIndex={10}
            sx={{ backgroundColor: 'rgba(255,255,255,0.7)' }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Base map (zIndex 0) */}
        {useStaticMap ? (
          <LebanonMapBaseStatic
            width={width} 
            height={height} 
            scale={scale}
            zoom={zoom}
            borderFC={borderFC}
            adm2FC={adm2FC}
            onMapReady={handleMapReady}
          />
        ) : (
          <LebanonMapBaseMapbox
            ref={mapboxRef}
            width={width} 
            height={height} 
            project={project}
            zoom={zoom}
            borderFC={borderFC}
            adm2FC={adm2FC}
            onMapReady={handleMapReady}
            interactive={interactive}
            mapStyle={mapStyle}
            showBorder={showBorder}
          />
        )}

        {/* Painted partitions (zIndex 1, visuals only) */}
      {showPartitions && <PartitionsLayer
          width={width}
          height={height}
          partitions={serverPartitions}
          currentStroke={currentStroke}
          color={color}
        />}

        {/* Hit-test overlay for deleting partitions (zIndex 2, below RF when not in delete mode) */}
         
          <svg
            width={width}
            height={height}
            style={{
              position: "absolute",
              inset: 0,
              zIndex: deleteMode && !partitionMode ? 4 : 2,
              pointerEvents: deleteMode && !partitionMode ? "auto" : "none",
            }}
          >
            {serverPartitions.map((p) => (
              <path
                key={`hit-${p.id}`}
                d={`M ${p.points.map((pt) => `${pt.x},${pt.y}`).join(" L ")} Z`}
                // tiny transparent fill ensures the polygon is clickable even if stroke is transparent
                fill="rgba(0,0,0,0.001)"
                stroke="transparent"
                style={{ cursor: deleteMode ? "pointer" : "default", pointerEvents: "auto" }}
                onClick={(evt) => openDeleteFor(evt, p.id)}
              />
            ))}
          </svg>
        

        {/* React Flow overlay (only when not using native routes) */}
        {!useNativeRoutes && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 3,
              pointerEvents: deleteMode || partitionMode ? "none" : "auto",
            }}
          >
            <RFOverlay
              nodes={rfNodes}
              edges={edges}
              nodeTypes={nodeTypes}
              partitionMode={false /* RF receives events only when wrapper allows */}
              onEdgeClick={(e, edge) => {
                // Wrapper blocks events when delete/partition modes are active
                e.stopPropagation();
                setEdgePopover({
                  open: true,
                  pos: { top: e.clientY, left: e.clientX },
                  edge,
                });
                setEdgeInputValue(edge.label ? String(edge.label) : '');
              }}
            />
          </div>
        )}


        {/* Popover for edge edit/delete */}
        <Popover
          open={edgePopover.open}
          onClose={edgeLoading ? undefined : closeEdgePopover}
          anchorReference="anchorPosition"
          anchorPosition={
            edgePopover.pos ? { top: edgePopover.pos.top, left: edgePopover.pos.left } : undefined
          }
        >
          <Stack spacing={1.25} sx={{ p: 1.5, minWidth: 240 }}>
            <Typography variant="subtitle2">
              Link: {edgePopover.edge?.source} → {edgePopover.edge?.target}
            </Typography>
            <TextField
              label="Label / ETA"
              size="small"
              value={edgeInputValue}
              onChange={(e) => setEdgeInputValue(e.target.value)}
              onKeyDown={(ev) => {
                if (ev.key === "Enter" && !edgeLoading) {
                  updateEdgeLabel(edgeInputValue.trim());
                }
              }}
              disabled={edgeLoading}
              autoFocus
            />
            <Stack direction="row" justifyContent="space-between">
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  updateEdgeLabel(edgeInputValue.trim());
                }}
                disabled={edgeLoading}
              >
                {edgeLoading ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="text"
                color="error"
                size="small"
                onClick={() => {
                  deleteEdge();
                }}
                disabled={edgeLoading}
              >
                {edgeLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </Stack>
          </Stack>
        </Popover>

        {/* Popover for partition delete confirmation (zIndex via portal, unaffected) */}
        <Popover
          open={deletePopover.open}
          onClose={closeDeletePopover}
          anchorReference="anchorPosition"
          anchorPosition={
            deletePopover.pos
              ? { top: deletePopover.pos.top, left: deletePopover.pos.left }
              : undefined
          }
        >
          <Stack spacing={1.25} sx={{ p: 1.5, minWidth: 240 }}>
            <Typography variant="subtitle2">Delete this partition?</Typography>
            <Stack direction="row" justifyContent="flex-end" gap={1}>
              <Button size="small" onClick={closeDeletePopover}>Cancel</Button>
              <Button size="small" color="error" variant="contained" onClick={confirmDeletePartition}>
                Delete
              </Button>
            </Stack>
          </Stack>
        </Popover>

        {/* Drawing capture layer (active only in partition mode, zIndex 5 topmost) */}
        
          <DrawCaptureLayer
            width={width}
            height={height}
            partitionMode={partitionMode}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
          />
      </div>
    </div>
  );
}

export default function LebanonMapFlow(props: LebanonMapFlowProps) {
  return (
    <ReactFlowProvider>
      <LebanonMapFlowInner {...props} />
    </ReactFlowProvider>
  );
}
